import React, { useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Link } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import Card from "shared/components/UIElements/Card";
import Input from "shared/components/FormElements/Input";
import Button from "shared/components/FormElements/Button";
import InputPhoneNumber from "components/ui/input/InputPhoneNumber";
import { VALIDATOR_REQUIRE, VALIDATOR_EMAIL, VALIDATOR_NONE, validate } from "shared/util/validators";
import { useForm } from "shared/hooks/form-hook";
import { getCurrentLanguage } from "shared/utility/lang";

import "./SmsOptIn.css";

const BUSINESS_NAME = "Velanor LLC";

const MixedSmsOptIn = () => {
  const { t } = useTranslation();
  const language = getCurrentLanguage();

  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("US");
  const [isPhoneValid, setIsPhoneValid] = useState(false);

  const [transactionalConsent, setTransactionalConsent] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const [formState, inputHandler] = useForm(
    {
      firstName: {
        value: "",
        isValid: false,
      },
      lastName: {
        value: "",
        isValid: false,
      },
      email: {
        value: "",
        isValid: true,
      },
    },
    false,
  );

  const phoneNumberChangeHandler = (phone, country, isValid) => {
    setPhoneNumber(phone);
    setCountryCode(country);
    setIsPhoneValid(isValid);
    setError("");
  };

  const canSubmit =
    formState.inputs.firstName.isValid &&
    formState.inputs.lastName.isValid &&
    isPhoneValid &&
    transactionalConsent &&
    termsAccepted;

  const handleSubmit = (event) => {
    event.preventDefault();
    setError("");

    const email = formState.inputs.email.value.trim();
    if (email && !validate(email, [VALIDATOR_EMAIL()])) {
      setError(t("mixedSmsOptIn.invalidEmail"));
      return;
    }

    if (!formState.inputs.firstName.isValid || !formState.inputs.lastName.isValid) {
      setError(t("mixedSmsOptIn.fillRequiredFields"));
      return;
    }

    if (!isPhoneValid) {
      setError(t("mixedSmsOptIn.invalidPhone"));
      return;
    }

    if (!termsAccepted || !transactionalConsent) {
      setError(t("mixedSmsOptIn.acceptConsentRequired"));
      return;
    }

    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (submitted) {
    return (
      <div className="sms-opt-in-page">
        <div className="sms-opt-in-container">
          <Card className="sms-opt-in-card sms-opt-in-card--success">
            <CheckCircleOutlineIcon className="sms-opt-in-success-icon" />
            <h1>{t("mixedSmsOptIn.successTitle")}</h1>
            <p>{t("mixedSmsOptIn.successMessage", { business: BUSINESS_NAME })}</p>
            <ul className="sms-opt-in-success-list">
              {transactionalConsent && <li>{t("mixedSmsOptIn.successTransactional")}</li>}
              {!transactionalConsent && <li>{t("mixedSmsOptIn.successNoSms")}</li>}
            </ul>
            <p className="sms-opt-in-legal-note">{t("mixedSmsOptIn.successLegalNote", { business: BUSINESS_NAME })}</p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="sms-opt-in-page">
      <div className="sms-opt-in-container">
        <Card className="sms-opt-in-card">
          <header className="sms-opt-in-header">
            <div className="sms-opt-in-logo" aria-hidden="true" />
            <p className="sms-opt-in-entity">{BUSINESS_NAME}</p>
            <h1 className="sms-opt-in-brand">
              RouteBot <span className="sms-opt-in-brand-sep">/</span> Servistesin
            </h1>
            <p className="sms-opt-in-tagline">{t("mixedSmsOptIn.campaignIntro", { business: BUSINESS_NAME })}</p>
            <p className="sms-opt-in-tagline">{t("mixedSmsOptIn.campaignMessages")}</p>
            <p className="sms-opt-in-tagline">{t("mixedSmsOptIn.campaignFrequency")}</p>
          </header>

          <h2 className="sms-opt-in-form-title">{t("mixedSmsOptIn.formTitle")}</h2>

          <form onSubmit={handleSubmit} noValidate>
            <div className="sms-opt-in-name-row">
              <Input
                element="input"
                id="firstName"
                type="text"
                label={t("mixedSmsOptIn.firstName")}
                validators={[VALIDATOR_REQUIRE()]}
                onInput={inputHandler}
                dontShowError
              />
              <Input
                element="input"
                id="lastName"
                type="text"
                label={t("mixedSmsOptIn.lastName")}
                validators={[VALIDATOR_REQUIRE()]}
                onInput={inputHandler}
                dontShowError
              />
            </div>

            <div className="sms-opt-in-phone-field">
              <InputPhoneNumber
                id="phoneNumber"
                label={t("mixedSmsOptIn.phoneNumber")}
                phoneNumber={phoneNumber}
                countryCode={countryCode}
                onChange={phoneNumberChangeHandler}
                errorText={t("mixedSmsOptIn.invalidPhone")}
                language={language}
                isEmptyAllowed={false}
                hideLabel
              />
            </div>

            <Input
              element="input"
              id="email"
              type="email"
              label={t("mixedSmsOptIn.emailOptional")}
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              dontShowError
            />

            <section className="sms-opt-in-consent-section" aria-label={t("mixedSmsOptIn.consentSectionLabel")}>
              <label className="sms-opt-in-consent-item" htmlFor="transactionalConsent">
                <input
                  type="checkbox"
                  id="transactionalConsent"
                  checked={transactionalConsent}
                  onChange={() => setTransactionalConsent((value) => !value)}
                />
                <p className="sms-opt-in-consent-text">
                  <Trans
                    i18nKey="mixedSmsOptIn.transactionalConsent"
                    values={{ business: BUSINESS_NAME }}
                    components={{ strong: <strong /> }}
                  />
                </p>
              </label>

              <label className="sms-opt-in-consent-item" htmlFor="termsAccepted">
                <input
                  type="checkbox"
                  id="termsAccepted"
                  checked={termsAccepted}
                  onChange={() => setTermsAccepted((value) => !value)}
                />
                <p className="sms-opt-in-consent-text">
                  <Trans
                    i18nKey="mixedSmsOptIn.termsConsent"
                    components={{
                      termsLink: <Link to="/terms" target="_blank" rel="noopener noreferrer" />,
                      privacyLink: <Link to="/privacy" target="_blank" rel="noopener noreferrer" />,
                    }}
                  />
                </p>
              </label>
            </section>

            {error && (
              <div className="sms-opt-in-error" role="alert">
                {error}
              </div>
            )}

            <div className="sms-opt-in-submit">
              <Button type="submit" disabled={!canSubmit}>
                {t("mixedSmsOptIn.continue")}
              </Button>
            </div>

            <p className="sms-opt-in-footnote">{t("mixedSmsOptIn.footnote", { business: BUSINESS_NAME })}</p>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default MixedSmsOptIn;
